import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setReviewHasError } from "actions/signup";
import ReviewFormRow from "./ReviewFormRow";
import SectionHead from "./SectionHead";
import TextInput from "components/Form/TextInput";
import InputGroup from "components/Form/InputGroup";
import Dropdown from "../../../components/Dropdown/Dropdown";
import { MailIcon } from "lucide-react";
import { booleanOptions, creatorContactOptions } from "consts/general";
import { isEmailValid } from "helpers/utils";
import get from "lodash/get";

const MgnNameFormRow = ({
  formData,
  onChange,
  onBlur,
  hasError,
  errorMessage,
}) => {
  return (
    <TextInput
      id="manager_name"
      name="manager_name"
      type="text"
      autoComplete="manager_name"
      placeholder="Manager Co"
      value={formData?.manager_name}
      onBlur={onBlur}
      onChange={(value) => onChange({ ...formData, manager_name: value })}
      hasError={hasError ? get(errorMessage, "manager_name", "") : null}
      required
    />
  );
};

const MgnEmailFormRow = ({
  formData,
  onChange,
  onBlur,
  hasError,
  errorMessage,
}) => {
  return (
    <InputGroup
      id="manager_email"
      name="manager_email"
      type="email"
      placeholder="manager@example.com"
      value={formData?.manager_email}
      onBlur={onBlur}
      onChange={(value) => onChange({ ...formData, manager_email: value })}
      hasError={hasError ? get(errorMessage, "manager_email", "") : null}
      Icon={() => <MailIcon className="h-5 w-5 text-gray-400" />}
    />
  );
};

const ReviewAgency = ({ onChange, onSubmit, formData }) => {
  const dispatch = useDispatch();
  const [errorMgnEmail, setErrorMgnEmail] = useState({});
  const [errorMgnName, setErrorMgnName] = useState({});
  const [errorMessage, setErrorMessage] = useState({});
  const [errorMgnReachOut, setErrorMgnReachOut] = useState({});
  const signupToken = useSelector((state) => state.signup.signupToken || null);

  const onManagerValidation = (value) => {
    const manager_represented =
      value || get(formData, "manager_represented", "");
    const manager_name = get(formData, "manager_name", "");
    const manager_email = get(formData, "manager_email", "");
    const manager_reach_out = get(formData, "manager_reach_out", "");
    if (manager_represented === null) {
      setErrorMessage({
        ...errorMessage,
        manager_represented: "Please selected an option",
      });
      return true;
    } else {
      if (
        manager_represented &&
        (!manager_name || !manager_email || !manager_reach_out)
      ) {
        dispatch(
          setReviewHasError({
            manager_represented: "Please fill in the information below",
          })
        );
        setErrorMessage({
          ...errorMessage,
          manager_represented: "Please fill in the information below",
        });
        return true;
      } else if (
        manager_represented &&
        manager_name &&
        manager_email &&
        manager_reach_out
      ) {
        dispatch(
          setReviewHasError({
            manager_represented: "",
          })
        );
        setErrorMessage({
          ...errorMessage,
          manager_represented: "",
        });
        return false;
      }
    }
    dispatch(
      setReviewHasError({
        manager_represented: "",
      })
    );
    setErrorMessage({ ...errorMessage, manager_represented: "" });
    return false;
  };

  const onManagerNameValidation = () => {
    const mgnName = get(formData, "manager_name", "");
    if (!mgnName) {
      setErrorMgnName({
        manager_name: "Please enter a name",
      });
      dispatch(
        setReviewHasError({
          manager_name: "Please enter a name",
        })
      );
      dispatch(
        setReviewHasError({
          manager_represented: "Please fill in the information below",
        })
      );
      return true;
    }
    dispatch(
      setReviewHasError({
        manager_name: "",
      })
    );
    setErrorMgnName({ manager_name: "" });
    onManagerValidation();
    return false;
  };

  const onManagerEmailValidation = () => {
    const email = get(formData, "manager_email", "");
    if (!email || !isEmailValid(email)) {
      setErrorMgnEmail({
        manager_email: !email
          ? "Please enter email address"
          : "Invalid email address",
      });
      dispatch(
        setReviewHasError({
          manager_email: !email
            ? "Please enter email address"
            : "Invalid email address",
        })
      );
      dispatch(
        setReviewHasError({
          manager_represented: "Please fill in the information below",
        })
      );
      return true;
    }
    dispatch(
      setReviewHasError({
        manager_email: "",
      })
    );
    setErrorMgnEmail({ manager_email: "" });
    onManagerValidation();
    return false;
  };

  const onManagerReachOutValidation = () => {
    const reachOut = get(formData, "manager_reach_out", "");
    if (!reachOut) {
      setErrorMgnReachOut({
        manager_reach_out: "Please enter an option",
      });
      dispatch(
        setReviewHasError({
          manager_reach_out: "Please enter a name",
        })
      );
      dispatch(
        setReviewHasError({
          manager_represented: "Please fill in the information below",
        })
      );
      return true;
    }
    dispatch(
      setReviewHasError({
        manager_reach_out: "",
      })
    );
    setErrorMgnReachOut({ manager_reach_out: "" });
    onManagerValidation();
    return false;
  };

  return (
    <section>
      <SectionHead
        title="Agency Information"
        subtitle="Agency contact information"
      />
      <div className="divide-y divide-gray-200">
        <ReviewFormRow
          label="Are you represented?"
          RowData={() => (
            <span>
              {
                booleanOptions.find(
                  (option) =>
                    (option.value === "yes" && formData?.manager_represented) ||
                    (option.value === "no" && !formData?.manager_represented)
                ).label
              }
            </span>
          )}
          onClick={() => {
            dispatch(
              onSubmit(
                {
                  signup_token: signupToken,
                  rewards_user: {
                    manager_represented: formData.manager_represented,
                  },
                },
                "registrations"
              )
            );
          }}
          EditForm={() => (
            <Dropdown
              placeholder="Please select"
              value={booleanOptions.find(
                (option) =>
                  (option.value === "yes" && formData?.manager_represented) ||
                  (option.value === "no" && !formData?.manager_represented)
              )}
              options={booleanOptions}
              onChange={(selected) => {
                if (selected?.value) {
                  onChange({
                    ...formData,
                    manager_represented:
                      selected.value === "yes" ? true : false,
                  });
                }
              }}
              error={get(errorMessage, "manager_represented", "")}
            />
          )}
          onValidate={onManagerValidation}
        />
        {formData?.manager_represented && (
          <>
            <ReviewFormRow
              label="Manager name"
              requestRequiredFields
              RowData={() => <span>{formData?.manager_name}</span>}
              onChange={(value) => {
                setErrorMgnName({ manager_name: "" });
                onChange(value);
              }}
              onClick={() => {
                dispatch(
                  onSubmit(
                    {
                      signup_token: signupToken,
                      rewards_user: {
                        manager_name: formData.manager_name,
                      },
                    },
                    "registrations"
                  )
                );
              }}
              formData={formData}
              EditForm={MgnNameFormRow}
              errorMessage={errorMgnName}
              onValidate={onManagerNameValidation}
            />
            <ReviewFormRow
              label="Manager email"
              requestRequiredFields
              RowData={() => <span>{formData?.manager_email}</span>}
              onChange={(value) => {
                setErrorMgnEmail({ manager_email: "" });
                onChange(value);
              }}
              onClick={() => {
                dispatch(
                  onSubmit(
                    {
                      signup_token: signupToken,
                      rewards_user: {
                        manager_email: formData.manager_email,
                      },
                    },
                    "registrations"
                  )
                );
              }}
              formData={formData}
              errorMessage={errorMgnEmail}
              EditForm={MgnEmailFormRow}
              onValidate={onManagerEmailValidation}
            />
            <ReviewFormRow
              label="Who do we reach out to?"
              requestRequiredFields
              RowData={() => (
                <span>
                  {
                    creatorContactOptions.find(
                      (option) => option.value === formData?.manager_reach_out
                    )?.label
                  }
                </span>
              )}
              onClick={() => {
                dispatch(
                  onSubmit(
                    {
                      signup_token: signupToken,
                      rewards_user: {
                        manager_reach_out: formData.manager_reach_out,
                      },
                    },
                    "registrations"
                  )
                );
              }}
              EditForm={() => (
                <Dropdown
                  placeholder="Please select"
                  options={creatorContactOptions}
                  value={creatorContactOptions.find(
                    (option) => option.value === formData?.manager_reach_out
                  )}
                  onChange={(selected) => {
                    if (selected?.value) {
                      setErrorMgnReachOut({ manager_reach_out: "" });
                      onChange({
                        ...formData,
                        manager_reach_out: selected.value,
                      });
                    }
                  }}
                  error={get(errorMgnReachOut, "manager_reach_out", "")}
                />
              )}
              errorMessage={errorMgnReachOut}
              onValidate={onManagerReachOutValidation}
            />
          </>
        )}
      </div>
    </section>
  );
};

export default ReviewAgency;
